'use client'

/**
 * Máquina de estados do chat comercial de pós-graduação (HN-3170).
 *
 * `useReducer` que conduz saudação → 4 perguntas → envio (Agendor +
 * Brevo) → conclusão. Portado do fluxo de captação da Atalia
 * (`useChatFlow`), simplificado: sem sub-passo de detalhamento — as 4
 * perguntas daqui não têm "Outro".
 *
 * Os botões da página de informações abrem o chat com `detail.source`;
 * essa origem vira `origem_contato` no Agendor e, para "Ver programa
 * completo", dispara o envio do programa por e-mail depois de o negócio
 * ser criado (ver `contact-origin.ts` e `program-material.ts`).
 */

import { useCallback, useEffect, useReducer, useRef } from 'react'
import { sendEventBrevoWithoutAuthAction } from '@/actions/send-event-brevo'
import {
  type AgendorContactOrigin,
  type AgendorFunnelConfig,
  formatGraduateDealDescription,
  getGraduateAnswerLabel,
  type GraduateAnswer,
  resolvePostgraduateAgendorFunnel,
} from '@/helpers/postgraduate-agendor-funnel'
import { createDealPeople } from '@/http/create-deal-people-agendor'
import { createPeople } from '@/http/create-people-agendor'
import {
  CHAT_COMPLETION_MESSAGE,
  CHAT_FLOW,
  CHAT_PROGRAM_MATERIAL_FAILED_MESSAGE,
  CHAT_PROGRAM_MATERIAL_SENT_MESSAGE,
  type ChatAnswers,
  type ChatStep,
  type ChatStepId,
} from '@/lib/postgraduate-chat/chat-flow'
import {
  isButtonContactOrigin,
  resolveChatContactOrigin,
  shouldSendProgramMaterial,
} from '@/lib/postgraduate-chat/contact-origin'
import { sendProgramMaterial } from '@/lib/postgraduate-chat/program-material'

// ─── Estado ───────────────────────────────────────────────────────────────────

export type ChatPhase =
  | 'fechado'
  | 'saudacao'
  | 'perguntando'
  | 'enviando'
  | 'concluido'
  | 'erroEnvio'

type ResumablePhase = Exclude<ChatPhase, 'fechado' | 'enviando'>

export interface ChatTranscriptEntry {
  readonly id: string
  readonly kind: 'agent' | 'user'
  readonly text: string
  readonly stepId?: ChatStepId
  readonly variant?: 'greeting' | 'question' | 'error' | 'completion'
}

export interface ChatState {
  readonly phase: ChatPhase
  readonly stepIndex: number
  readonly answers: ChatAnswers
  readonly transcript: readonly ChatTranscriptEntry[]
  readonly started: boolean
  /** Persistido — trava reenvio ao Agendor em reabertura futura. */
  readonly completed: boolean
  readonly submissionError?: string
  readonly lastActivePhase?: ResumablePhase
  /** Botão que abriu o chat — vira `origem_contato`. Persistido. */
  readonly contactOrigin?: AgendorContactOrigin
  /**
   * Chat já concluído reaberto por "Ver programa completo": reenvia só o
   * material com as respostas salvas, sem novo negócio no Agendor.
   */
  readonly pendingProgramMaterial: boolean
}

/** Resultado do envio do programa completo, já como mensagem do atendente. */
export interface ProgramMaterialDelivery {
  readonly success: boolean
  readonly message: string
}

export type ChatAction =
  | {
      type: 'OPEN'
      payload: { greetingMessages: readonly string[]; source?: string }
    }
  | { type: 'START' }
  | { type: 'ANSWER'; payload: { stepId: ChatStepId; value: string } }
  | { type: 'SELECT_OPTION'; payload: { stepId: ChatStepId; value: string } }
  | { type: 'SUBMIT_SUCCESS'; payload: { material?: ProgramMaterialDelivery } }
  | { type: 'PROGRAM_MATERIAL_RESULT'; payload: ProgramMaterialDelivery }
  | { type: 'SUBMIT_ERROR'; payload: { message: string } }
  | { type: 'RETRY' }
  | { type: 'CLOSE' }
  | { type: 'RESTORE'; payload: PersistedChatState }

// ─── Persistência ─────────────────────────────────────────────────────────────

const STORAGE_VERSION = 1

export interface PersistedChatState {
  readonly version: number
  readonly stepIndex: number
  readonly answers: ChatAnswers
  readonly transcript: readonly ChatTranscriptEntry[]
  readonly started: boolean
  readonly completed: boolean
  readonly lastActivePhase?: ResumablePhase
  readonly contactOrigin?: AgendorContactOrigin
}

const RESUMABLE_PHASES: readonly ResumablePhase[] = [
  'saudacao',
  'perguntando',
  'concluido',
  'erroEnvio',
]

function isValidPersistedChatState(data: unknown): data is PersistedChatState {
  if (!data || typeof data !== 'object') {
    return false
  }
  const candidate = data as Record<string, unknown>

  if (candidate.version !== STORAGE_VERSION) {
    return false
  }
  if (
    typeof candidate.stepIndex !== 'number' ||
    !Number.isInteger(candidate.stepIndex) ||
    candidate.stepIndex < 0 ||
    candidate.stepIndex > CHAT_FLOW.length
  ) {
    return false
  }
  if (typeof candidate.started !== 'boolean' || typeof candidate.completed !== 'boolean') {
    return false
  }
  if (!Array.isArray(candidate.transcript)) {
    return false
  }
  if (!candidate.answers || typeof candidate.answers !== 'object') {
    return false
  }
  if (
    candidate.lastActivePhase !== undefined &&
    !RESUMABLE_PHASES.includes(candidate.lastActivePhase as ResumablePhase)
  ) {
    return false
  }
  if (
    candidate.contactOrigin !== undefined &&
    !isButtonContactOrigin(candidate.contactOrigin)
  ) {
    return false
  }
  return true
}

function loadPersistedChatState(storageKey: string): PersistedChatState | null {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    const raw = window.sessionStorage.getItem(storageKey)
    if (!raw) {
      return null
    }
    const parsed: unknown = JSON.parse(raw)
    return isValidPersistedChatState(parsed) ? parsed : null
  } catch {
    return null
  }
}

function toPersistedChatState(state: ChatState): PersistedChatState {
  return {
    version: STORAGE_VERSION,
    stepIndex: state.stepIndex,
    answers: state.answers,
    transcript: state.transcript,
    started: state.started,
    completed: state.completed,
    lastActivePhase: state.lastActivePhase,
    contactOrigin: state.contactOrigin,
  }
}

// ─── Estado inicial ───────────────────────────────────────────────────────────

function createInitialChatState(): ChatState {
  return {
    phase: 'fechado',
    stepIndex: 0,
    answers: {},
    transcript: [],
    started: false,
    completed: false,
    submissionError: undefined,
    lastActivePhase: undefined,
    contactOrigin: undefined,
    pendingProgramMaterial: false,
  }
}

// ─── Transcript — helpers imutáveis ───────────────────────────────────────────

function seedGreeting(greetingMessages: readonly string[]): ChatTranscriptEntry[] {
  return greetingMessages.map((text, i) => ({
    id: `greeting-${i}`,
    kind: 'agent' as const,
    text,
    variant: 'greeting' as const,
  }))
}

function appendAgentQuestion(
  transcript: readonly ChatTranscriptEntry[],
  step: ChatStep
): ChatTranscriptEntry[] {
  const entries = step.messages.map((text, i) => ({
    id: `${step.id}-question-${i}`,
    kind: 'agent' as const,
    text,
    stepId: step.id,
    variant: 'question' as const,
  }))
  return [...transcript, ...entries]
}

function upsertUserEntry(
  transcript: readonly ChatTranscriptEntry[],
  id: string,
  stepId: ChatStepId,
  text: string
): ChatTranscriptEntry[] {
  const entry: ChatTranscriptEntry = { id, kind: 'user', text, stepId }
  const existingIndex = transcript.findIndex(e => e.id === id)
  if (existingIndex === -1) {
    return [...transcript, entry]
  }
  const next = [...transcript]
  next[existingIndex] = entry
  return next
}

function upsertAnswerEntry(
  transcript: readonly ChatTranscriptEntry[],
  stepId: ChatStepId,
  value: string
): ChatTranscriptEntry[] {
  return upsertUserEntry(transcript, `${stepId}-answer`, stepId, value)
}

function appendErrorEntry(
  transcript: readonly ChatTranscriptEntry[],
  stepId: ChatStepId,
  message: string
): ChatTranscriptEntry[] {
  const entry: ChatTranscriptEntry = {
    id: `${stepId}-error-${transcript.length}`,
    kind: 'agent',
    text: message,
    stepId,
    variant: 'error',
  }
  return [...transcript, entry]
}

function appendCompletionMessage(
  transcript: readonly ChatTranscriptEntry[]
): ChatTranscriptEntry[] {
  return [
    ...transcript,
    { id: 'completion', kind: 'agent', text: CHAT_COMPLETION_MESSAGE, variant: 'completion' },
  ]
}

function appendProgramMaterialEntry(
  transcript: readonly ChatTranscriptEntry[],
  delivery: ProgramMaterialDelivery
): ChatTranscriptEntry[] {
  return [
    ...transcript,
    {
      id: `program-material-${transcript.length}`,
      kind: 'agent',
      text: delivery.message,
      variant: delivery.success ? 'completion' : 'error',
    },
  ]
}

// ─── Reducer ──────────────────────────────────────────────────────────────────

function advanceStep(
  state: ChatState,
  idx: number,
  answers: ChatAnswers,
  transcript: ChatTranscriptEntry[]
): ChatState {
  const nextIndex = idx + 1

  if (nextIndex >= CHAT_FLOW.length) {
    return { ...state, answers, transcript, stepIndex: nextIndex, phase: 'enviando' }
  }

  const nextStep = CHAT_FLOW[nextIndex]
  return {
    ...state,
    answers,
    stepIndex: nextIndex,
    transcript: appendAgentQuestion(transcript, nextStep),
  }
}

function handleAnswer(state: ChatState, stepId: ChatStepId, rawValue: string): ChatState {
  const idx = CHAT_FLOW.findIndex(s => s.id === stepId)
  if (idx === -1 || idx !== state.stepIndex) {
    // Passo já respondido (fluxo simplificado — sem reedição de texto livre).
    return state
  }
  const step = CHAT_FLOW[idx]
  if (step.answerType === 'options') {
    return state
  }

  const validation = step.validate(rawValue)
  if (!validation.ok) {
    return { ...state, transcript: appendErrorEntry(state.transcript, step.id, validation.message) }
  }

  const transcript = upsertAnswerEntry(state.transcript, step.id, validation.value)
  const answers = { ...state.answers, [step.id]: validation.value }
  return advanceStep(state, idx, answers, transcript)
}

function handleSelectOption(state: ChatState, stepId: ChatStepId, rawValue: string): ChatState {
  const idx = CHAT_FLOW.findIndex(s => s.id === stepId)
  if (idx === -1 || idx !== state.stepIndex) {
    return state
  }
  const step = CHAT_FLOW[idx]
  if (step.answerType !== 'options') {
    return state
  }

  const validation = step.validate(rawValue)
  if (!validation.ok) {
    return { ...state, transcript: appendErrorEntry(state.transcript, step.id, validation.message) }
  }

  const answers = { ...state.answers, [step.id]: validation.value }
  const optionLabel =
    step.options?.find(option => option.value === validation.value)?.label ?? validation.value
  const transcript = upsertAnswerEntry(state.transcript, step.id, optionLabel)
  return advanceStep(state, idx, answers, transcript)
}

function handleOpen(
  state: ChatState,
  greetingMessages: readonly string[],
  source: string | undefined
): ChatState {
  // Só um botão da página redefine a origem; launcher e auto-open
  // preservam a atual.
  const contactOrigin = isButtonContactOrigin(source)
    ? source
    : state.contactOrigin
  // Chat já concluído + clique em "Ver programa completo" → só reenvia
  // o material, sem novo negócio.
  const pendingProgramMaterial =
    state.pendingProgramMaterial ||
    (state.completed && shouldSendProgramMaterial(source))

  if (state.phase !== 'fechado') {
    if (
      contactOrigin === state.contactOrigin &&
      pendingProgramMaterial === state.pendingProgramMaterial
    ) {
      return state
    }
    return { ...state, contactOrigin, pendingProgramMaterial }
  }
  if (state.completed) {
    return {
      ...state,
      phase: 'concluido',
      contactOrigin,
      pendingProgramMaterial,
    }
  }

  const resumePhase: ResumablePhase =
    state.lastActivePhase ?? (state.started ? 'perguntando' : 'saudacao')

  const transcript =
    resumePhase === 'saudacao' && state.transcript.length === 0
      ? seedGreeting(greetingMessages)
      : state.transcript

  return {
    ...state,
    phase: resumePhase,
    transcript,
    contactOrigin,
    pendingProgramMaterial,
  }
}

function chatFlowReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'OPEN': {
      return handleOpen(
        state,
        action.payload.greetingMessages,
        action.payload.source
      )
    }

    case 'START': {
      if (state.phase !== 'saudacao') {
        return state
      }
      const firstStep = CHAT_FLOW[0]
      return {
        ...state,
        phase: 'perguntando',
        started: true,
        stepIndex: 0,
        transcript: appendAgentQuestion(state.transcript, firstStep),
      }
    }

    case 'ANSWER': {
      if (state.phase !== 'perguntando') {
        return state
      }
      return handleAnswer(state, action.payload.stepId, action.payload.value)
    }

    case 'SELECT_OPTION': {
      if (state.phase !== 'perguntando') {
        return state
      }
      return handleSelectOption(state, action.payload.stepId, action.payload.value)
    }

    case 'SUBMIT_SUCCESS': {
      const { material } = action.payload
      const transcript = appendCompletionMessage(state.transcript)
      return {
        ...state,
        phase: 'concluido',
        completed: true,
        submissionError: undefined,
        transcript: material
          ? appendProgramMaterialEntry(transcript, material)
          : transcript,
      }
    }

    case 'PROGRAM_MATERIAL_RESULT': {
      return {
        ...state,
        pendingProgramMaterial: false,
        transcript: appendProgramMaterialEntry(
          state.transcript,
          action.payload
        ),
      }
    }

    case 'SUBMIT_ERROR': {
      return { ...state, phase: 'erroEnvio', submissionError: action.payload.message }
    }

    case 'RETRY': {
      if (state.phase !== 'erroEnvio') {
        return state
      }
      return { ...state, phase: 'enviando', submissionError: undefined }
    }

    case 'CLOSE': {
      if (state.phase === 'enviando' || state.phase === 'fechado') {
        return state
      }
      return { ...state, phase: 'fechado', lastActivePhase: state.phase as ResumablePhase }
    }

    case 'RESTORE': {
      const { payload } = action
      return {
        ...state,
        stepIndex: payload.stepIndex,
        answers: payload.answers,
        transcript: [...payload.transcript],
        started: payload.started,
        completed: payload.completed,
        lastActivePhase: payload.lastActivePhase,
        contactOrigin: payload.contactOrigin,
        pendingProgramMaterial: false,
        phase: 'fechado',
      }
    }

    default:
      return state
  }
}

// ─── Envio ────────────────────────────────────────────────────────────────────

export interface PostgraduateChatContext {
  readonly brandName?: string | null
  readonly agendorFunnel?: AgendorFunnelConfig | null
  readonly courseId?: string
  readonly courseName?: string
  readonly courseAmount?: string | number | null
  /** Imagem do curso — vai no e-mail do programa completo. */
  readonly courseImage?: string | null
  readonly originPage?: string
}

type SubmitResult =
  | { success: true; material?: ProgramMaterialDelivery }
  | { success: false; error: string }

/** Envia o programa completo e traduz o resultado em mensagem do atendente. */
async function deliverProgramMaterial(
  answers: ChatAnswers,
  context: PostgraduateChatContext
): Promise<ProgramMaterialDelivery> {
  const name = answers.name?.trim()
  const email = answers.email

  if (!(name && email)) {
    return { success: false, message: CHAT_PROGRAM_MATERIAL_FAILED_MESSAGE }
  }

  const result = await sendProgramMaterial({
    email,
    name,
    phone: answers.phone,
    courseId: context.courseId,
    courseName: context.courseName,
    courseImage: context.courseImage,
  })

  return result.success
    ? { success: true, message: CHAT_PROGRAM_MATERIAL_SENT_MESSAGE }
    : { success: false, message: CHAT_PROGRAM_MATERIAL_FAILED_MESSAGE }
}

async function submitPostgraduateLead(
  answers: ChatAnswers,
  context: PostgraduateChatContext,
  contactOrigin: AgendorContactOrigin | undefined
): Promise<SubmitResult> {
  const name = answers.name ?? ''
  const email = answers.email
  const phone = answers.phone
  const graduateAnswer = (answers.graduate ?? 'sim') as GraduateAnswer
  const whatsapp = phone ? `+${phone}` : undefined

  try {
    const { data: person } = await createPeople({
      name,
      contact: { email, whatsapp },
    })

    const funnel = resolvePostgraduateAgendorFunnel({
      brandName: context.brandName,
      agendorFunnel: context.agendorFunnel,
    })

    await createDealPeople({
      personId: person.id,
      dealAgendor: {
        title: `${person.id} - ${name}`,
        products: context.courseName ? [context.courseName] : undefined,
        funnel,
        description: formatGraduateDealDescription(context.courseName, graduateAnswer),
        value: context.courseAmount ? Number(context.courseAmount) : undefined,
        customFields: {
          origem_contato: resolveChatContactOrigin(contactOrigin),
          pos_graduacao_desejada: context.courseName ?? '',
          'voce_ja_e_graduado?': getGraduateAnswerLabel(graduateAnswer),
        },
      },
    })

    if (email) {
      await sendEventBrevoWithoutAuthAction({
        event: 'interest_pos',
        email,
        eventdata: {
          data: {
            course_id: context.courseId,
            course_name: context.courseName,
            email,
            nome: name,
            whatsapp: phone ?? '',
          },
        },
        properties: {
          course_name: context.courseName,
          nome: name,
          whatsapp: phone ?? '',
        },
      })
    }

    // Pedido explícito: o material só sai DEPOIS de o negócio existir no
    // Agendor, e uma falha aqui não invalida o lead — vira aviso no chat.
    if (!shouldSendProgramMaterial(contactOrigin)) {
      return { success: true }
    }
    return {
      success: true,
      material: await deliverProgramMaterial(answers, context),
    }
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Erro desconhecido ao enviar seus dados.'
    return { success: false, error: message }
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const POSTGRADUATE_CHAT_OPEN_EVENT_NAME = 'openPostgraduateChat'

/**
 * Abre o chat de qualquer lugar da página. `source` é o botão clicado
 * (valor de `AGENDOR_CONTACT_ORIGIN`) ou um rótulo interno (launcher,
 * auto-open) — só os primeiros chegam ao Agendor.
 */
export function dispatchPostgraduateChatOpen(source: string): void {
  window.dispatchEvent(
    new CustomEvent(POSTGRADUATE_CHAT_OPEN_EVENT_NAME, { detail: { source } })
  )
}

export interface UsePostgraduateChatOptions {
  readonly storageKey: string
  readonly greetingMessages: readonly string[]
  readonly context: PostgraduateChatContext
}

export interface UsePostgraduateChatResult {
  readonly state: ChatState
  readonly currentStep: ChatStep | null
  readonly answer: (stepId: ChatStepId, value: string) => void
  readonly selectOption: (stepId: ChatStepId, value: string) => void
  readonly start: () => void
  readonly retry: () => void
  readonly close: () => void
  readonly open: (source?: string) => void
}

export function usePostgraduateChat({
  storageKey,
  greetingMessages,
  context,
}: UsePostgraduateChatOptions): UsePostgraduateChatResult {
  const [state, dispatch] = useReducer(chatFlowReducer, undefined, createInitialChatState)

  const hasHydratedRef = useRef(false)
  const stateRef = useRef(state)
  const contextRef = useRef(context)
  const greetingMessagesRef = useRef(greetingMessages)
  const submitLockRef = useRef(false)
  const materialLockRef = useRef(false)

  useEffect(() => {
    stateRef.current = state
  }, [state])

  useEffect(() => {
    contextRef.current = context
  }, [context])

  useEffect(() => {
    greetingMessagesRef.current = greetingMessages
  }, [greetingMessages])

  // Restaura do sessionStorage uma única vez, depois da montagem.
  useEffect(() => {
    if (hasHydratedRef.current) {
      return
    }
    hasHydratedRef.current = true
    const persisted = loadPersistedChatState(storageKey)
    if (persisted) {
      dispatch({ type: 'RESTORE', payload: persisted })
    }
  }, [storageKey])

  // Persiste a cada mudança relevante.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(toPersistedChatState(state)))
    } catch {
      // sessionStorage indisponível (modo privado, quota etc.) — segue só em memória.
    }
  }, [state, storageKey])

  const open = useCallback((source?: string) => {
    dispatch({
      type: 'OPEN',
      payload: { greetingMessages: greetingMessagesRef.current, source },
    })
  }, [])

  // Escuta o evento de abertura (launcher, auto-open e botões da página —
  // estes últimos informam o botão clicado em `detail.source`).
  useEffect(() => {
    function handleOpenEvent(event: Event) {
      const source = (event as CustomEvent<{ source?: unknown }>).detail?.source
      if (typeof source === 'string') {
        open(source)
        return
      }
      open()
    }
    window.addEventListener(POSTGRADUATE_CHAT_OPEN_EVENT_NAME, handleOpenEvent)
    return () =>
      window.removeEventListener(
        POSTGRADUATE_CHAT_OPEN_EVENT_NAME,
        handleOpenEvent
      )
  }, [open])

  const answer = useCallback((stepId: ChatStepId, value: string) => {
    dispatch({ type: 'ANSWER', payload: { stepId, value } })
  }, [])

  const selectOption = useCallback((stepId: ChatStepId, value: string) => {
    dispatch({ type: 'SELECT_OPTION', payload: { stepId, value } })
  }, [])

  const start = useCallback(() => dispatch({ type: 'START' }), [])
  const retry = useCallback(() => dispatch({ type: 'RETRY' }), [])
  const close = useCallback(() => dispatch({ type: 'CLOSE' }), [])

  // Dispara o envio exatamente uma vez por entrada em `enviando`.
  useEffect(() => {
    if (state.phase !== 'enviando') {
      submitLockRef.current = false
      return
    }
    if (submitLockRef.current) {
      return
    }
    submitLockRef.current = true

    let cancelled = false
    ;(async () => {
      const result = await submitPostgraduateLead(
        stateRef.current.answers,
        contextRef.current,
        stateRef.current.contactOrigin
      )
      if (cancelled) {
        return
      }
      if (result.success) {
        dispatch({
          type: 'SUBMIT_SUCCESS',
          payload: { material: result.material },
        })
      } else {
        dispatch({ type: 'SUBMIT_ERROR', payload: { message: result.error } })
      }
    })()

    return () => {
      cancelled = true
    }
  }, [state.phase])

  // Reenvio do programa completo em chat já concluído — uma vez por
  // pedido (mesmo lock do envio principal).
  useEffect(() => {
    if (!state.pendingProgramMaterial) {
      materialLockRef.current = false
      return
    }
    if (materialLockRef.current) {
      return
    }
    materialLockRef.current = true

    let cancelled = false
    ;(async () => {
      const delivery = await deliverProgramMaterial(
        stateRef.current.answers,
        contextRef.current
      )
      if (cancelled) {
        return
      }
      dispatch({ type: 'PROGRAM_MATERIAL_RESULT', payload: delivery })
    })()

    return () => {
      cancelled = true
    }
  }, [state.pendingProgramMaterial])

  const currentStep = state.stepIndex < CHAT_FLOW.length ? CHAT_FLOW[state.stepIndex] : null

  return { state, currentStep, answer, selectOption, start, retry, close, open }
}
